import React, { useState, useEffect, useCallback } from 'react';
import { Form, Input, Button, Select, InputNumber, Switch, Modal, Table, Space, Popconfirm } from 'antd';
import { MinusCircleOutlined, PlusOutlined, LeftCircleOutlined, EditOutlined, EyeOutlined, DeleteOutlined } from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLoading } from '../../context/LoadingContext';
import {apiGetVDSProviders, apiUpdateVDSProvider} from '../../api';
import { toastError, toastSuccess } from '../../utils/toast';

const INPUT_STYLE = {
  width: '90%',
};

const ICON_STYLE = {
  marginLeft: 8,
  fontSize: '16px',
  color: '#999'
};

interface ChannelInfo {
  channel_id?: number;
  channel_type?: string;
  source?: string;
  website_address?: string;
  website_name?: string;
  shop_code?: string;
  shop_name?: string;
  shop_address?: string;
  staff_id?: string;
  staff_id_no?: string;
  staff_mobile_number?: string;
  app_name?: string;
  atm_number?: string;
  atm_address?: string;
  acc_no?: string;
  bank_code?: string;
  mobile_number?: string;
  ewallet_isdn?: string;
  other_info?: string;
}

interface UpdateVDSProviderRequest {
  name: string;
  soapEndpoint: string;
  username?: string;
  password?: string;
  nccPublicKeyPem?: string;
  partnerPrivateKeyPem?: string;
  timeout?: number;
  namespaceApi?: string;
  soapEnvelopeTemplate?: string;
  status?: string;
  isActive?: boolean;
  channelInfoList?: ChannelInfo[];
  proxyAddress?: string[];
  otpConfirm: string;
}

const statusOptions = [
  { label: 'Hoạt động', value: 'active' },
  { label: 'Không hoạt động', value: 'inactive' },
];

const rules = {
  name: [
    { required: true, message: 'Vui lòng nhập tên nhà cung cấp' }
  ],
  soapEndpoint: [
    { required: true, message: 'Vui lòng nhập SOAP Endpoint' },
    { type: 'url' as const, message: 'Vui lòng nhập URL hợp lệ' }
  ],
  otpConfirm: [
    { required: true, message: 'Vui lòng nhập mã OTP' },
    { min: 6, message: 'Mã OTP phải có ít nhất 6 ký tự' },
  ],
};

export default function EditVDSProvider() {
  const [form] = Form.useForm<UpdateVDSProviderRequest>();
  const [channelForm] = Form.useForm<ChannelInfo>();
  const navigate = useNavigate();
  const location = useLocation();
  const { setIsLoading } = useLoading();
  const [submitting, setSubmitting] = useState(false);
  const [channelList, setChannelList] = useState<ChannelInfo[]>([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalMode, setModalMode] = useState<'add' | 'edit' | 'view'>('add');
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [providerName, setProviderName] = useState<string>('');

  const fetchProviderDetail = useCallback(async (name: string) => {
    try {
      setIsLoading(true);
      const response = await apiGetVDSProviders();
      if (response.status === 200 && response.data.vdsProviders) {
        // Find the provider by name from the list
        const provider = response.data.vdsProviders.find((p: any) => p.name === name);
        if (provider) {
          form.setFieldsValue({
            name: provider.name,
            soapEndpoint: provider.soapEndpoint,
            username: provider.username,
            password: provider.password,
            nccPublicKeyPem: provider.nccPublicKeyPem,
            partnerPrivateKeyPem: provider.partnerPrivateKeyPem,
            timeout: provider.timeout || 30,
            namespaceApi: provider.namespaceApi,
            soapEnvelopeTemplate: provider.soapEnvelopeTemplate,
            status: provider.status || 'active',
            isActive: provider.isActive !== undefined ? provider.isActive : true,
            proxyAddress: provider.proxyAddress && provider.proxyAddress.length > 0 ? provider.proxyAddress : [''],
          });
          setChannelList(provider.channelInfoList || []);
        } else {
          toastError('Không tìm thấy nhà cung cấp');
          navigate(-1);
        }
      }
    } catch (error: any) {
      console.error('Error fetching provider detail:', error);
      toastError('Không thể tải thông tin nhà cung cấp');
    } finally {
      setIsLoading(false);
    }
  }, [form, setIsLoading, navigate]);

  useEffect(() => {
    // Get provider name from location state or query params
    const name = location.state?.name || new URLSearchParams(location.search).get('name');
    if (name) {
      setProviderName(name);
      fetchProviderDetail(name);
    } else {
      toastError('Không tìm thấy tên nhà cung cấp');
      navigate(-1);
    }
  }, [location, fetchProviderDetail, navigate]);

  const handleSubmit = async (values: UpdateVDSProviderRequest) => {
    try {
      setSubmitting(true);
      setIsLoading(true);

      // Clean up arrays
      const cleanedValues = {
        ...values,
        name: providerName, // Use the original name as identifier
        proxyAddress: cleanArray(values.proxyAddress || []),
        channelInfoList: channelList,
      };

      const resp = await apiUpdateVDSProvider(cleanedValues);
      if (resp.status === 200) {
        toastSuccess('Cập nhật dữ liệu thành công!');
        navigate(-1);
      }
    } catch (error: any) {
      console.error('Error updating VDS provider:', error);
      toastError('Vui lòng kiểm tra lại thông tin!');
    } finally {
      setSubmitting(false);
      setIsLoading(false);
    }
  };

  const cleanArray = (arr: string[]) => {
    return Array.from(new Set(arr.filter(item => item && item.trim() !== '')));
  };

  const handleAddChannel = () => {
    setModalMode('add');
    setEditingIndex(null);
    channelForm.resetFields();
    setModalVisible(true);
  };

  const handleEditChannel = (record: ChannelInfo, index: number) => {
    setModalMode('edit');
    setEditingIndex(index);
    channelForm.setFieldsValue(record);
    setModalVisible(true);
  };

  const handleViewChannel = (record: ChannelInfo) => {
    setModalMode('view');
    channelForm.setFieldsValue(record);
    setModalVisible(true);
  };

  const handleDeleteChannel = (index: number) => {
    const newList = channelList.filter((_, i) => i !== index);
    setChannelList(newList);
    toastSuccess('Xóa channel thành công!');
  };

  const handleModalOk = () => {
    channelForm.validateFields().then((values) => {
      if (modalMode === 'add') {
        setChannelList([...channelList, values]);
        toastSuccess('Thêm channel thành công!');
      } else if (modalMode === 'edit' && editingIndex !== null) {
        const newList = [...channelList];
        newList[editingIndex] = values;
        setChannelList(newList);
        toastSuccess('Cập nhật channel thành công!');
      }
      setModalVisible(false);
      channelForm.resetFields();
    });
  };

  const handleModalCancel = () => {
    setModalVisible(false);
    channelForm.resetFields();
  };

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 200, marginBottom: 20 }}>
        <Button
          onClick={() => navigate(-1)}
          icon={<LeftCircleOutlined />}
          style={{ background: 'gray', color: 'white' }}
        >
          Trở lại
        </Button>
      </div>
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <Form
          style={{ width: '700px' }}
          form={form}
          onFinish={handleSubmit}
          labelAlign="left"
          labelCol={{ span: 6 }}
          wrapperCol={{ span: 18 }}
          initialValues={{
            timeout: 30,
            status: 'active',
            isActive: true,
          }}
        >
          <Form.Item
            label="Tên nhà cung cấp"
            name="name"
            rules={rules.name}
          >
            <Input placeholder="Nhập tên nhà cung cấp" style={INPUT_STYLE} disabled />
          </Form.Item>

          <Form.Item
            label="SOAP Endpoint"
            name="soapEndpoint"
            rules={rules.soapEndpoint}
          >
            <Input placeholder="https://example.com/soap" style={INPUT_STYLE} />
          </Form.Item>

          <Form.Item
            label="Username"
            name="username"
          >
            <Input placeholder="Nhập username" style={INPUT_STYLE} />
          </Form.Item>

          <Form.Item
            label="Password"
            name="password"
          >
            <Input.Password placeholder="Nhập password" style={INPUT_STYLE} />
          </Form.Item>

          <Form.Item
            label="NCC Public Key PEM"
            name="nccPublicKeyPem"
          >
            <Input.TextArea
              placeholder="Nhập NCC Public Key PEM"
              style={INPUT_STYLE}
              rows={4}
            />
          </Form.Item>

          <Form.Item
            label="Partner Private Key PEM"
            name="partnerPrivateKeyPem"
          >
            <Input.TextArea
              placeholder="Nhập Partner Private Key PEM"
              style={INPUT_STYLE}
              rows={4}
            />
          </Form.Item>

          <Form.Item
            label="Timeout (giây)"
            name="timeout"
          >
            <InputNumber
              style={INPUT_STYLE}
              placeholder="Nhập timeout"
              min={1}
            />
          </Form.Item>

          <Form.Item
            label="Namespace API"
            name="namespaceApi"
          >
            <Input placeholder="Nhập namespace API" style={INPUT_STYLE} />
          </Form.Item>

          <Form.Item
            label="SOAP Envelope Template"
            name="soapEnvelopeTemplate"
          >
            <Input.TextArea
              placeholder="Nhập SOAP envelope template"
              style={INPUT_STYLE}
              rows={6}
            />
          </Form.Item>

          <Form.Item
            label="Trạng thái"
            name="status"
          >
            <Select
              placeholder="Chọn trạng thái"
              options={statusOptions}
              style={INPUT_STYLE}
            />
          </Form.Item>

          <Form.Item
            label="Kích hoạt"
            name="isActive"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          {/* Proxy Address List */}
          <Form.List name="proxyAddress" initialValue={['']}>
            {(fields, { add, remove }) => (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {fields.map((field, index) => (
                  <Form.Item
                    label={index === 0 ? "Proxy Address" : " "}
                    colon={index === 0}
                    key={field.key}
                  >
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <Form.Item
                        {...field}
                        validateTrigger={['onChange', 'onBlur']}
                        noStyle
                      >
                        <Input
                          placeholder="Nhập địa chỉ proxy"
                          style={INPUT_STYLE}
                        />
                      </Form.Item>
                      <div style={{ whiteSpace: 'nowrap' }}>
                        {fields.length > 1 && (
                          <MinusCircleOutlined
                            className="dynamic-delete-button"
                            onClick={() => remove(field.name)}
                            style={ICON_STYLE}
                          />
                        )}
                        {index === fields.length - 1 && (
                          <PlusOutlined
                            onClick={() => add()}
                            style={ICON_STYLE}
                          />
                        )}
                      </div>
                    </div>
                  </Form.Item>
                ))}
              </div>
            )}
          </Form.List>

          <Form.Item
            label="Mã OTP xác nhận"
            name="otpConfirm"
            rules={rules.otpConfirm}
          >
            <Input placeholder="Nhập OTP xác nhận" style={INPUT_STYLE} />
          </Form.Item>
        </Form>
      </div>

      {/* Channel Info List - Full Width */}
      <div style={{ marginTop: 24, marginBottom: 24 }}>
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0 }}>Channel Info List</h3>
          <Button
            type="dashed"
            onClick={handleAddChannel}
            icon={<PlusOutlined />}
          >
            Thêm channel
          </Button>
        </div>
        {channelList.length > 0 && (
          <Table
            dataSource={channelList}
            rowKey={(record, index) => index?.toString() || '0'}
            pagination={false}
            size="small"
            scroll={{ x: 'max-content' }}
            bordered
            columns={[
              {
                title: 'STT',
                key: 'index',
                width: 50,
                align: 'center',
                render: (_, __, index) => index + 1,
              },
              {
                title: 'Channel ID',
                dataIndex: 'channel_id',
                key: 'channel_id',
                width: 100,
              },
              {
                title: 'Channel Type',
                dataIndex: 'channel_type',
                key: 'channel_type',
                width: 120,
              },
              {
                title: 'Source',
                dataIndex: 'source',
                key: 'source',
                width: 100,
              },
              {
                title: 'Shop Name',
                dataIndex: 'shop_name',
                key: 'shop_name',
                width: 150,
              },
              {
                title: 'Website Name',
                dataIndex: 'website_name',
                key: 'website_name',
                width: 150,
              },
              {
                title: 'App Name',
                dataIndex: 'app_name',
                key: 'app_name',
                width: 120,
              },
              {
                title: 'Staff ID',
                dataIndex: 'staff_id',
                key: 'staff_id',
                width: 100,
              },
              {
                title: 'Mobile Number',
                dataIndex: 'mobile_number',
                key: 'mobile_number',
                width: 130,
              },
              {
                title: 'Bank Code',
                dataIndex: 'bank_code',
                key: 'bank_code',
                width: 100,
              },
              {
                title: 'Thao tác',
                key: 'action',
                width: 120,
                fixed: 'right',
                align: 'center',
                render: (_, record, index) => (
                  <Space size="small">
                    <Button
                      type="text"
                      icon={<EyeOutlined />}
                      onClick={() => handleViewChannel(record)}
                      style={{ color: '#52c41a' }}
                    />
                    <Button
                      type="text"
                      icon={<EditOutlined />}
                      onClick={() => handleEditChannel(record, index)}
                      style={{ color: '#1890ff' }}
                    />
                    <Popconfirm
                      title="Xóa channel"
                      description="Bạn có chắc chắn muốn xóa channel này?"
                      onConfirm={() => handleDeleteChannel(index)}
                      okText="Xóa"
                      cancelText="Hủy"
                      okButtonProps={{ danger: true }}
                    >
                      <Button
                        type="text"
                        icon={<DeleteOutlined />}
                        danger
                      />
                    </Popconfirm>
                  </Space>
                ),
              },
            ]}
          />
        )}
      </div>

      {/* Submit Button */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 24 }}>
        <Button
          type="primary"
          onClick={() => form.submit()}
          loading={submitting}
        >
          Cập nhật
        </Button>
      </div>

      {/* Channel Info Modal */}
      <Modal
        title={
          modalMode === 'add' ? 'Thêm Channel' :
          modalMode === 'edit' ? 'Cập nhật Channel' :
          'Xem thông tin Channel'
        }
        open={modalVisible}
        onOk={modalMode === 'view' ? handleModalCancel : handleModalOk}
        onCancel={handleModalCancel}
        width={800}
        okText={modalMode === 'view' ? 'Đóng' : modalMode === 'add' ? 'Thêm' : 'Cập nhật'}
        cancelText={modalMode === 'view' ? undefined : 'Hủy'}
        cancelButtonProps={{ style: modalMode === 'view' ? { display: 'none' } : {} }}
        centered
      >
        <Form
          form={channelForm}
          labelAlign="left"
          labelCol={{ span: 8 }}
          wrapperCol={{ span: 16 }}
          disabled={modalMode === 'view'}
        >
          {/* Basic Info */}
          <div style={{ marginBottom: 16 }}>
            <div style={{
              fontSize: '14px',
              fontWeight: 'bold',
              color: '#1890ff',
              marginBottom: 12,
              paddingBottom: 8,
              borderBottom: '2px solid #1890ff'
            }}>
              Thông tin cơ bản
            </div>
            <Form.Item label="Channel ID" name="channel_id">
              <InputNumber placeholder="Nhập channel ID" style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item label="Channel Type" name="channel_type">
              <Input placeholder="Nhập channel type" />
            </Form.Item>
            <Form.Item label="Source" name="source">
              <Input placeholder="Nhập source" />
            </Form.Item>
          </div>

          {/* Website Info */}
          <div style={{ marginBottom: 16 }}>
            <div style={{
              fontSize: '14px',
              fontWeight: 'bold',
              color: '#1890ff',
              marginBottom: 12,
              paddingBottom: 8,
              borderBottom: '2px solid #1890ff'
            }}>
              Thông tin Website
            </div>
            <Form.Item label="Website Address" name="website_address">
              <Input placeholder="Nhập website address" />
            </Form.Item>
            <Form.Item label="Website Name" name="website_name">
              <Input placeholder="Nhập website name" />
            </Form.Item>
          </div>

          {/* Shop Info */}
          <div style={{ marginBottom: 16 }}>
            <div style={{
              fontSize: '14px',
              fontWeight: 'bold',
              color: '#1890ff',
              marginBottom: 12,
              paddingBottom: 8,
              borderBottom: '2px solid #1890ff'
            }}>
              Thông tin Cửa hàng
            </div>
            <Form.Item label="Shop Code" name="shop_code">
              <Input placeholder="Nhập shop code" />
            </Form.Item>
            <Form.Item label="Shop Name" name="shop_name">
              <Input placeholder="Nhập shop name" />
            </Form.Item>
            <Form.Item label="Shop Address" name="shop_address">
              <Input placeholder="Nhập shop address" />
            </Form.Item>
          </div>

          {/* Staff Info */}
          <div style={{ marginBottom: 16 }}>
            <div style={{
              fontSize: '14px',
              fontWeight: 'bold',
              color: '#1890ff',
              marginBottom: 12,
              paddingBottom: 8,
              borderBottom: '2px solid #1890ff'
            }}>
              Thông tin Nhân viên
            </div>
            <Form.Item label="Staff ID" name="staff_id">
              <Input placeholder="Nhập staff ID" />
            </Form.Item>
            <Form.Item label="Staff ID Number" name="staff_id_no">
              <Input placeholder="Nhập staff ID number" />
            </Form.Item>
            <Form.Item label="Staff Mobile" name="staff_mobile_number">
              <Input placeholder="Nhập staff mobile number" />
            </Form.Item>
          </div>

          {/* App & ATM Info */}
          <div style={{ marginBottom: 16 }}>
            <div style={{
              fontSize: '14px',
              fontWeight: 'bold',
              color: '#1890ff',
              marginBottom: 12,
              paddingBottom: 8,
              borderBottom: '2px solid #1890ff'
            }}>
              Thông tin App & ATM
            </div>
            <Form.Item label="App Name" name="app_name">
              <Input placeholder="Nhập app name" />
            </Form.Item>
            <Form.Item label="ATM Number" name="atm_number">
              <Input placeholder="Nhập ATM number" />
            </Form.Item>
            <Form.Item label="ATM Address" name="atm_address">
              <Input placeholder="Nhập ATM address" />
            </Form.Item>
          </div>

          {/* Bank & Payment Info */}
          <div style={{ marginBottom: 16 }}>
            <div style={{
              fontSize: '14px',
              fontWeight: 'bold',
              color: '#1890ff',
              marginBottom: 12,
              paddingBottom: 8,
              borderBottom: '2px solid #1890ff'
            }}>
              Thông tin Ngân hàng & Thanh toán
            </div>
            <Form.Item label="Account No" name="acc_no">
              <Input placeholder="Nhập account number" />
            </Form.Item>
            <Form.Item label="Bank Code" name="bank_code">
              <Input placeholder="Nhập bank code" />
            </Form.Item>
            <Form.Item label="Mobile Number" name="mobile_number">
              <Input placeholder="Nhập mobile number" />
            </Form.Item>
            <Form.Item label="E-Wallet ISDN" name="ewallet_isdn">
              <Input placeholder="Nhập e-wallet ISDN" />
            </Form.Item>
          </div>

          {/* Other Info */}
          <div>
            <div style={{
              fontSize: '14px',
              fontWeight: 'bold',
              color: '#1890ff',
              marginBottom: 12,
              paddingBottom: 8,
              borderBottom: '2px solid #1890ff'
            }}>
              Thông tin khác
            </div>
            <Form.Item label="Other Info" name="other_info">
              <Input.TextArea placeholder="Nhập thông tin khác" rows={3} />
            </Form.Item>
          </div>
        </Form>
      </Modal>
    </>
  );
}

